/**
 * StepCard (#122) — the tooltip card shown beside the spotlighted anchor.
 *
 * Renders the current step's title + body, a clear "Step X of Y" header with a
 * compact "you are here" progress bar + dots, and the Back / Next / Skip-tour
 * controls. The card positions itself relative to the anchor rect using the
 * step's `placement` (top/bottom/left/right), clamped to the viewport so it
 * never spills off-screen. Token-styled (Card + Button), z-50 so it sits above
 * the Spotlight dim.
 *
 * COMPLETION (#122 follow-up): when `celebrating` is true (the user finished
 * the final step), the card swaps to a short celebration — a check, a "Tour
 * complete!" line, and Done / Back-to-tutorials actions — before it closes and
 * marks the tour done. Esc / the X still exit at any time.
 */

import { useLayoutEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Check, GraduationCap, Loader2, RotateCcw, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/lib/ui/button";
import { cn } from "@/lib/ui/cn";
import type { StepPlacement } from "./definitions";

/** Card dimensions used for placement math (max width; height measured live). */
const CARD_WIDTH = 320;
/** Gap between the anchor box and the card, in px. */
const GAP = 16;
/** Min margin from the viewport edge, in px. */
const MARGIN = 12;

export interface StepCardProps {
  title: string;
  body: string;
  stepIndex: number;
  stepCount: number;
  placement?: StepPlacement;
  /** Anchor rect to position against; null → center the card. */
  rect: DOMRect | null;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
  /** Whether the current step is the tour's last (button label + finish flow). */
  isLast: boolean;
  /**
   * When true, render the completion celebration instead of the step body.
   * Set by the overlay once the user presses the final step's button.
   */
  celebrating?: boolean;
  /** Tour title, shown in the celebration header. */
  tutorialTitle?: string;
  /** Confirm completion: marks the tour done + closes. */
  onFinish?: () => void;
  /** Restart the tour from step 1 (offered in the celebration). */
  onRestart?: () => void;
  /** True when the tour was resumed past its first step (shows a hint). */
  resumed?: boolean;
  /**
   * True while a required anchor is still being located (not yet found, not yet
   * timed out). Shows a soft "looking for this on the page" note so a momentarily
   * empty spotlight reads as "loading", never as "broken". The tour auto-skips
   * if it ultimately can't find it (handled by the overlay).
   */
  searching?: boolean;
}

/** Compute the card's top/left given the anchor rect, placement, and size. */
function placeCard(
  rect: DOMRect | null,
  placement: StepPlacement,
  size: { width: number; height: number },
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // No anchor yet → center it.
  if (!rect) {
    return {
      top: (vh - size.height) / 2,
      left: (vw - size.width) / 2,
    };
  }

  let top: number;
  let left: number;
  switch (placement) {
    case "top":
      top = rect.top - size.height - GAP;
      left = rect.left + rect.width / 2 - size.width / 2;
      break;
    case "left":
      top = rect.top + rect.height / 2 - size.height / 2;
      left = rect.left - size.width - GAP;
      break;
    case "right":
      top = rect.top + rect.height / 2 - size.height / 2;
      left = rect.right + GAP;
      break;
    case "bottom":
    default:
      top = rect.bottom + GAP;
      left = rect.left + rect.width / 2 - size.width / 2;
      break;
  }

  // Clamp to viewport so the card is always fully visible.
  left = Math.min(Math.max(MARGIN, left), vw - size.width - MARGIN);
  top = Math.min(Math.max(MARGIN, top), vh - size.height - MARGIN);
  return { top, left };
}

export function StepCard({
  title,
  body,
  stepIndex,
  stepCount,
  placement = "bottom",
  rect,
  onBack,
  onNext,
  onSkip,
  isLast,
  celebrating = false,
  tutorialTitle,
  onFinish,
  onRestart,
  resumed = false,
  searching = false,
}: StepCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // The celebration is a wrap-up, not tied to any one element → center it.
  const effectiveRect = celebrating ? null : rect;
  const effectivePlacement = celebrating ? "bottom" : placement;

  // Position after layout so we can measure the rendered card height. Re-runs
  // whenever the anchor rect, placement, or content changes (incl. celebrate).
  useLayoutEffect(() => {
    const height = cardRef.current?.offsetHeight ?? 180;
    setPos(
      placeCard(effectiveRect, effectivePlacement, { width: CARD_WIDTH, height }),
    );
  }, [effectiveRect, effectivePlacement, title, body, celebrating]);

  const isFirst = stepIndex === 0;
  // Steps completed so far for the "you are here" bar. On the celebration view
  // every step is behind the user, so the bar reads full.
  const completed = celebrating ? stepCount : stepIndex;
  const pct = stepCount > 0 ? Math.round((completed / stepCount) * 100) : 0;

  return (
    <div
      ref={cardRef}
      role="dialog"
      aria-label={
        celebrating
          ? `Tour complete: ${tutorialTitle ?? title}`
          : `Tutorial step ${stepIndex + 1} of ${stepCount}: ${title}`
      }
      className={cn(
        "fixed z-50 flex w-80 flex-col gap-3 rounded-lg border border-border",
        "bg-surface-elevated p-4 text-on-surface shadow-xl animate-fade-in",
      )}
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        // Hide until positioned to avoid a flash at the wrong spot.
        visibility: pos ? "visible" : "hidden",
      }}
    >
      {celebrating ? (
        <CelebrationBody
          tutorialTitle={tutorialTitle ?? title}
          stepCount={stepCount}
          onFinish={onFinish ?? onSkip}
          onRestart={onRestart}
        />
      ) : (
        <>
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-[11px] font-medium uppercase tracking-wider text-brand">
                Step {stepIndex + 1} of {stepCount}
              </span>
              <h2 className="text-base font-semibold leading-tight text-on-surface">
                {title}
              </h2>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="-mr-1 -mt-1 size-7 shrink-0 text-on-surface-muted"
              aria-label="Skip tutorial"
              onClick={onSkip}
            >
              <X className="size-4" />
            </Button>
          </div>

          {resumed && (
            <p className="-mt-1 text-[11px] font-medium text-on-surface-muted">
              Resumed where you left off.
            </p>
          )}

          {searching && (
            <p className="-mt-1 flex items-center gap-1.5 text-[11px] font-medium text-on-surface-muted">
              <Loader2 className="size-3 animate-spin" aria-hidden />
              Looking for this on the page…
            </p>
          )}

          <p className="text-sm leading-relaxed text-on-surface-muted">{body}</p>

          {/* "You are here" progress — a labelled bar plus per-step dots. */}
          <div className="flex flex-col gap-1.5">
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-surface-muted"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={stepCount}
              aria-valuenow={stepIndex + 1}
              aria-label={`Step ${stepIndex + 1} of ${stepCount}`}
            >
              <div
                className="h-full rounded-full bg-brand transition-all"
                style={{ width: `${Math.max(pct, 100 / stepCount)}%` }}
              />
            </div>
            <div className="flex items-center gap-1.5" aria-hidden>
              {Array.from({ length: stepCount }, (_, i) => (
                <span
                  key={i}
                  className={cn(
                    "h-1.5 rounded-full transition-all",
                    i === stepIndex
                      ? "w-5 bg-brand"
                      : i < stepIndex
                        ? "w-1.5 bg-brand/50"
                        : "w-1.5 bg-surface-muted",
                  )}
                />
              ))}
            </div>
          </div>

          <div className="mt-1 flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-on-surface-muted"
              onClick={onSkip}
            >
              Skip tour
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onBack}
                disabled={isFirst}
              >
                <ArrowLeft className="mr-1 size-3.5" />
                Back
              </Button>
              <Button variant="primary" size="sm" onClick={onNext}>
                {isLast ? "Finish" : "Next"}
                {!isLast && <ArrowRight className="ml-1 size-3.5" />}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The completion celebration shown on the final step. A check badge, a
 * congratulatory line, and the wrap-up actions: Done (marks complete + closes)
 * and Restart (replay from step 1). Also links back to the Tutorials hub.
 */
function CelebrationBody({
  tutorialTitle,
  stepCount,
  onFinish,
  onRestart,
}: {
  tutorialTitle: string;
  stepCount: number;
  onFinish: () => void;
  onRestart?: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-1 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-success/15 text-success animate-fade-in">
        <Check className="size-6" strokeWidth={3} />
      </div>
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold leading-tight text-on-surface">
          Tour complete!
        </h2>
        <p className="text-sm leading-relaxed text-on-surface-muted">
          You finished{" "}
          <span className="font-medium text-on-surface">{tutorialTitle}</span> —
          all {stepCount} steps. Nice work.
        </p>
      </div>

      <div className="mt-1 flex w-full flex-col gap-2">
        <Button variant="primary" size="sm" className="w-full" onClick={onFinish}>
          <Check className="mr-1.5 size-3.5" />
          Done
        </Button>
        <div className="flex items-center gap-2">
          {onRestart && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={onRestart}
            >
              <RotateCcw className="mr-1.5 size-3.5" />
              Restart
            </Button>
          )}
          <Button asChild variant="ghost" size="sm" className="flex-1">
            <Link to="/app/settings?tab=help" onClick={onFinish}>
              <GraduationCap className="mr-1.5 size-3.5" />
              Tutorials
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
